import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ContentChild,
  TemplateRef,
  ElementRef,
  ChangeDetectionStrategy,
  ViewChild,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { FadeInOut } from '@app/animations/fade.animation';
import { Screen, WindowService } from '@modules/shared/services/window.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  RibbonSliderFilterInterface,
  RibbonSliderNavigationConfigInterface,
} from '../interfaces/ribbon-slider.interface';
import { SliderUtilUpdateNavigation } from '../utils/slider.utils';

@Component({
  selector: 'mb-ribbon-slider-section',
  templateUrl: './ribbon-slider-section.component.html',
  styleUrls: ['./ribbon-slider-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: FadeInOut,
})
export class RibbonSliderSectionComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<boolean>();
  private _items: any[] = [];
  public canLeft = false;
  public canRight = false;
  public activeIndex = 0;

  public activeConfig: RibbonSliderNavigationConfigInterface = {
    showNavigation: false,
    itemsToShow: 0,
    navigationCap: 0,
  };
  private _config: RibbonSliderNavigationConfigInterface = this.activeConfig;

  @Input() mobileFilter = false;
  @Input() filterWrapperClasses: string[] = ['col-12', 'col-md-3'];
  @Input() placeholderOffsetItemClasses: string[] = ['d-none'];
  @Input() offsetItemClasses: string[] = ['d-none'];
  @Input() itemWrapperClasses: string[] = ['col-12', 'col-xs-6', 'col-sm-7', 'col-md-5', 'col-xl-4'];
  @Input() containerClass = 'container-xxl';

  @Input()
  set config(_config: RibbonSliderNavigationConfigInterface) {
    this._config = _config;
    const config = this.getActiveScreenConfig(this.windowService.screen);
    this.setConfig(config);
  }
  get config() {
    return this._config;
  }

  @Input() filters: RibbonSliderFilterInterface[] = [];

  @HostListener('class.sicky-navigation')
  @Input()
  stickyFilters = false;

  @Input()
  set items(v: any[]) {
    this._items = v;
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollLeft = 0;
    }
    this.canLeft = false;
    this.canRight = this._items.length > this.activeConfig.itemsToShow;
    this.activeIndex = 0;
  }
  get items() {
    return this._items;
  }

  @ContentChild(TemplateRef, { static: true }) itemTemplate!: TemplateRef<any>;
  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef<any>;
  @ViewChild('cardItem', { static: false }) cardItem!: ElementRef<any>;
  @ViewChild('navContainer', { static: false }) navContainer!: ElementRef<any>;
  @ViewChild('navigation', { static: false }) navigation!: ElementRef<any>;
  @ViewChild('offsetCard', { static: false }) offsetCard!: ElementRef<any>;

  @Output() filtered = new EventEmitter<RibbonSliderFilterInterface>();

  constructor(private cdr: ChangeDetectorRef, private windowService: WindowService) {}

  public setFilter(filter: RibbonSliderFilterInterface) {
    this.filters.forEach((filter) => (filter.active = false));
    filter.active = true;
    this.filtered.emit(filter);
  }

  public scrollPrev() {
    this.slideTo(this.getIndex() - 1);
  }

  public scrollNext() {
    this.slideTo(this.getIndex() + 1);
  }

  private getIndex() {
    let widthToSubsctract = 0;
    const scrollLeft = this.scrollContainer.nativeElement.scrollLeft;
    if (!scrollLeft) {
      return 0;
    }
    widthToSubsctract += this.navContainer ? this.navContainer.nativeElement.clientWidth : 0;
    if (this.offsetCard.nativeElement) {
      const display = getComputedStyle(this.offsetCard.nativeElement).display;
      widthToSubsctract -= display !== 'none' ? this.offsetCard.nativeElement.clientWidth : 0;
    }
    const itemWidth = this.cardItem.nativeElement.clientWidth;
    return Math.ceil((scrollLeft - widthToSubsctract) / itemWidth);
  }

  public slideTo(index: number) {
    if (index <= 0) {
      this.scrollContainer.nativeElement.scrollLeft = 0;
      return;
    }
    let widthToSubsctract = 0;
    this.activeIndex = index;
    const itemWidth = this.cardItem.nativeElement.clientWidth;
    let newLeft = index * itemWidth;

    widthToSubsctract -= this.navContainer ? this.navContainer.nativeElement.clientWidth : 0;
    if (this.offsetCard.nativeElement) {
      const display = getComputedStyle(this.offsetCard.nativeElement).display;
      widthToSubsctract += display !== 'none' ? this.offsetCard.nativeElement.clientWidth : 0;
    }
    this.scrollContainer.nativeElement.scrollLeft = newLeft - widthToSubsctract;
  }

  private checkCanScrollLeftRight() {
    let hasChange = false;
    const scrollContainer = this.scrollContainer.nativeElement;
    const canLeft = scrollContainer.scrollLeft > 0;
    const canRight = scrollContainer.scrollLeft + scrollContainer.offsetWidth < scrollContainer.scrollWidth;
    if (this.canLeft !== canLeft) {
      this.canLeft = canLeft;
      hasChange = true;
    }
    if (this.canRight !== canRight) {
      this.canRight = canRight;
      hasChange = true;
    }
    if (hasChange) {
      this.cdr.detectChanges();
    }

    if (this.navigation) {
      const cb = SliderUtilUpdateNavigation(this._items.length, 2, this.activeIndex);
      this.navigation.nativeElement.childNodes.forEach(cb);
    }
  }

  public getActiveScreenConfig(screen: Screen): RibbonSliderNavigationConfigInterface {
    let config: RibbonSliderNavigationConfigInterface | null = null;
    if (this.config?.breakpoints) {
      if (this.config.breakpoints[screen]) {
        config = this.config.breakpoints[screen] as RibbonSliderNavigationConfigInterface;
      } else {
        const keys = Object.keys(this.config.breakpoints).map((key) => parseInt(key, 10)) as unknown as number[];
        const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
        if (possibleConfigs?.length) {
          config = this.config.breakpoints[
            possibleConfigs[possibleConfigs.length - 1]
          ] as RibbonSliderNavigationConfigInterface;
        }
      }
    }
    if (!config) {
      config = this.config;
    }
    return config as RibbonSliderNavigationConfigInterface;
  }

  private setConfig(config: RibbonSliderNavigationConfigInterface) {
    const { navigationCap, showNavigation, itemsToShow } = config || this.activeConfig;
    this.activeConfig.navigationCap = navigationCap || this._config?.navigationCap;
    this.activeConfig.showNavigation = showNavigation || this._config?.showNavigation;
    this.activeConfig.itemsToShow = itemsToShow || this._config?.itemsToShow;
    setTimeout(() => {
      this.checkCanScrollLeftRight();
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    const cb = () => {
      this.checkCanScrollLeftRight();
      const index = this.getIndex();
      if (index !== this.activeIndex) {
        this.activeIndex = index;
        this.cdr.detectChanges();
      }
    };
    this.scrollContainer.nativeElement.addEventListener('scroll', cb);
    this.destroy$.pipe(take(1)).subscribe(() => {
      this.scrollContainer.nativeElement.removeEventListener('scroll', cb);
    });
    this.windowService.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      const config = this.getActiveScreenConfig(screen);
      this.setConfig(config);
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
